import { Link } from 'react-router-dom';
import { CalendarOff, ClipboardCheck, UserCheck, Users, UserX } from 'lucide-react';
import { Card } from '../../../components/Card';
import { EmptyState } from '../../../components/EmptyState';
import { UserAvatar } from '../../../components/UserAvatar';
import type { DayStatus, PeopleDashboardData } from '../../../store/peopleDashboardStore';
import { ApprovalQueuePanel, DayStatusBadge, Kpi, Panel, SectionHeading, fmtHours, fmtTime } from './widgets';

/** Working first, then checked out, on leave, not in — the people a manager needs to notice. */
const STATUS_ORDER: Record<DayStatus, number> = { checked_in: 0, checked_out: 1, on_leave: 2, not_checked_in: 3 };

/** Reporting Manager: direct reports' day and the approvals waiting on them. */
export function TeamSection({ data }: { data: PeopleDashboardData }) {
  const team = data.team;

  if (!team) {
    return (
      <section className="space-y-4">
        <SectionHeading title="My team" />
        <EmptyState
          icon={<Users />}
          title="Team view needs an employee profile"
          description="Your account is not linked to an employee record, so your direct reports cannot be found. Please contact HR."
        />
      </section>
    );
  }

  const approvals = team.pendingLeave.total + team.pendingRegularizations.total;
  const members = [...team.members].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name));

  return (
    <section className="space-y-4">
      <SectionHeading title="My team" description="Your direct reports today" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Kpi label="Team size" value={team.size} icon={<Users />} tone="primary" hint="Direct reports" />
        <Kpi label="Present" value={team.present} icon={<UserCheck />} tone="success"
          hint={team.size ? `${Math.round((team.present / team.size) * 100)}% of team` : '—'} />
        <Kpi label="On leave" value={team.onLeave} icon={<CalendarOff />} tone="warning" hint="Approved leave today" />
        <Kpi label="Not checked in" value={team.notCheckedIn} icon={<UserX />} hint="Yet to check in today" />
        <Kpi label="Approvals" value={approvals} icon={<ClipboardCheck />} tone={approvals ? 'warning' : 'default'}
          to="/people/leave" hint={`${team.pendingLeave.total} leave · ${team.pendingRegularizations.total} attendance`} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {team.size === 0 ? (
          <Card className="lg:col-span-8">
            <EmptyState
              className="h-full border-0"
              icon={<Users />}
              title="No direct reports yet"
              description="People appear here once you are set as their reporting manager on their employee profile."
            />
          </Card>
        ) : (
          <Panel
            title="Team today"
            description={`${team.present} of ${team.size} present`}
            link={{ label: 'Attendance', to: '/people/attendance' }}
            className="lg:col-span-8"
            bodyClassName="px-0 pb-0"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-border bg-secondary/50 text-left text-sm text-muted-foreground">
                    <th className="h-9 px-4 font-semibold">Employee</th>
                    <th className="h-9 px-4 font-semibold">Status</th>
                    <th className="h-9 px-4 font-semibold">First check-in</th>
                    <th className="h-9 px-4 text-right font-semibold">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members.map((m) => (
                    <tr key={m.id} className="h-10 hover:bg-secondary/40">
                      <td className="px-4 py-2">
                        <Link to={`/people/employees/${m.id}`} className="flex items-center gap-2.5 hover:text-primary">
                          <UserAvatar name={m.name} size="sm" />
                          <span className="min-w-0">
                            <span className="block truncate text-foreground">{m.name}</span>
                            {m.designation && <span className="block truncate text-xs text-muted-foreground">{m.designation}</span>}
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-2"><DayStatusBadge status={m.status} /></td>
                      <td className="px-4 py-2 tabular-nums text-muted-foreground">{fmtTime(m.checkinAt)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{m.hours ? fmtHours(m.hours) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        <ApprovalQueuePanel
          leave={team.pendingLeave}
          regularizations={team.pendingRegularizations}
          today={data.today}
          scopeLabel="From your direct reports"
          className="lg:col-span-4"
        />
      </div>
    </section>
  );
}
